import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Switch } from '@/shared/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'@/shared/components/ui/select';
import {
  FileText,
  Settings,
  Building2,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  Save,
  Edit,
  Cog,
  Loader2 } from
'lucide-react';

import { oigAPI } from '@/shared/services/api';

const defaultConfig = {
  enabled: true,
  environment: 'production',
  certificate_valid: true,
  certificate_expiry: '2027-12-31',
  auto_download: true,
  auto_manifest: true,
  cnpj: '12.345.678/0001-90',
  user: { usuario: '', senha: '', grupo: '', serie: '' },
  companies: []
};

export const NFeConfiguration = () => {const { t } = useTranslation("oig");
  const [config, setConfig] = useState(defaultConfig);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setLoading(true);
        const data = await oigAPI.getConfig();
        setConfig((prev) => ({ ...prev, ...data }));
      } catch (err) {
        console.error('Failed to fetch NF-e config:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="oig-nfe-config">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk'] flex items-center gap-3">
            <FileText className="h-8 w-8 text-emerald-600" />{t("configuraoDeNfe", "Configura\xE7\xE3o de NF-e")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("configuraoDeEmisso", "Configura\xE7\xE3o de Emiss\xE3o de Nota Fiscal Eletr\xF4nica")}

          </p>
        </div>
      </div>

      {/* User Configuration */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-muted-foreground" />{t("usurioEmissorDe", "Usu\xE1rio Emissor de Nota Fiscal")}

          </CardTitle>
          <CardDescription>{t("configureAsCredenciais", "Configure as credenciais do usu\xE1rio emissor para integra\xE7\xE3o com a SEFAZ")}

          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("usurio", "Usu\xE1rio")}
                <span className="text-red-500">*</span>
              </label>
              <Input
                value={config.user.usuario}
                placeholder="admin"
                className="font-mono" />
              
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("senha", "Senha")}
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={config.user.senha}
                  placeholder="••••••••"
                  className="font-mono pr-10" />
                
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full"
                  onClick={() => setShowPassword(!showPassword)}>
                  
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("grupo", "Grupo")}
                <span className="text-red-500">*</span>
              </label>
              <Input
                value={config.user.grupo}
                placeholder="Grupo" />
              
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("srie", "S\xE9rie")}</label>
              <Input
                value={config.user.serie}
                placeholder="1" />
              
            </div>
          </div>
          <div className="flex justify-end">
            <Button className="bg-emerald-600 hover:bg-emerald-700 gap-2">
              <Save className="h-4 w-4" />{t("salvarUsurio", "Salvar Usu\xE1rio")}

            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Companies Configuration */}
      <Card className="card-elevated">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-muted-foreground" />{t("empresasEmissorasDe", "Empresas Emissoras de Nota Fiscal")}

            </CardTitle>
            <CardDescription>{t("gerencieAsEmpresas", "Gerencie as empresas que podem emitir NF-e")}

            </CardDescription>
          </div>
          <Button className="bg-emerald-600 hover:bg-emerald-700 gap-2">
            <Plus className="h-4 w-4" />{t("adicionarEmpresa", "Adicionar Empresa")}

          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">{t("cnpj", "CNPJ")}</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">{t("identificao", "Identifica\xE7\xE3o")}</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">{t("nomeFantasia", "Nome Fantasia")}</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">{t("aes", "A\xE7\xF5es")}</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">{t("ambiente", "Ambiente")}</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">{t("2Nota", "2\xAA Nota")}</th>
                </tr>
              </thead>
              <tbody>
                {config.companies.map((company) =>
                <tr key={company.id} className="border-b border-border hover:bg-muted/30">
                    <td className="py-4 px-4">
                      <span className="font-mono text-sm">{company.cnpj}</span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="font-medium">{company.identificacao}</span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-sm text-muted-foreground">{company.nomeFantasia}</span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex gap-2">
                        <Button size="sm" className="bg-amber-500 hover:bg-amber-600 h-8">
                          <Edit className="h-3 w-3 mr-1" />{t("editar", "Editar")}

                      </Button>
                        <Button size="sm" variant="destructive" className="h-8">
                          <Trash2 className="h-3 w-3 mr-1" />{t("excluir", "Excluir")}

                      </Button>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <Select defaultValue={company.ambiente}>
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Produção">{t("produo", "Produ\xE7\xE3o")}</SelectItem>
                          <SelectItem value="Homologação">{t("homologao", "Homologa\xE7\xE3o")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-4 px-4">
                      <Button variant="outline" size="icon">
                        <Cog className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>);

};